/**
 * Client entry point: wires the socket, the store, the narrator and the
 * renderer together, and implements the `Actions` surface the screens call.
 *
 * Everything below is plumbing. The rules live on the server, and the screens
 * are pure functions of the snapshot it sends - which is why a player can
 * refresh mid-round and land exactly where they left off.
 *
 * A device may hold two seats. Player commands therefore name the seat they
 * act for; host commands resolve the one host seat this device happens to own.
 */

import { ROOM_CODE_LENGTH, type RoomSettings } from "../shared/constants.js";
import type { PlayerId, SeatedMessage, ServerMessage } from "../shared/protocol.js";
import type { CardSlot, RoleId } from "../shared/roles.js";
import type { Actions } from "./actions.js";
import { errorMessage } from "./i18n/fr.js";
import { Narrator } from "./narrator.js";
import { GameSocket, type ConnectionStatus } from "./net/socket.js";
import { Store } from "./store.js";
import { createRenderer } from "./ui/render.js";
import { applyTap } from "./ui/screens/night.js";
import { presetJoinCode, resetHomeDraft } from "./ui/screens/home.js";

const root = document.getElementById("app");
if (!root) throw new Error("Missing #app root element");

const store = new Store();
const narrator = new Narrator();
narrator.setVoice(store.state.voice);

// Browsers only let a page make sound once it has been touched, so every tap
// offers to arm the narrator until one of them succeeds. Listening here, and
// not in the handful of actions that precede a line, also covers the host who
// reloads mid-round: they create no room and start no game before the next
// role is called.
for (const gesture of ["click", "touchend", "keydown"]) {
  document.addEventListener(gesture, () => narrator.unlock(), { capture: true, passive: true });
}

const socket = new GameSocket({
  onMessage: (message) => handleMessage(message),
  onStatus: (status: ConnectionStatus) => store.setStatus(status),
});

/** Sends a command on behalf of one seat this device holds. */
function sendAs(seat: PlayerId, message: SeatedMessage): void {
  socket.send({ ...message, seat });
}

/**
 * Sends a host-only command from whichever seat on this device is the host.
 * Silently does nothing otherwise - the UI never offers the control anyway,
 * and host status can change under a stale render.
 */
function sendAsHost(message: SeatedMessage): void {
  const seat = store.hostSeatId();
  if (seat) sendAs(seat, message);
}

const actions: Actions = {
  createRoom(nickname) {
    store.setNickname(nickname);
    store.setError(null);
    socket.send({ t: "create_room", nickname });
  },

  joinRoom(code, nickname) {
    store.setNickname(nickname);
    store.setError(null);
    socket.send({ t: "join_room", code, nickname });
  },

  addPlayer(nickname) {
    store.setError(null);
    store.setAddingPlayer(false);
    socket.send({ t: "add_player", nickname });
  },

  leaveRoom(seat) {
    socket.send(seat ? { t: "leave_room", seat } : { t: "leave_room" });
    if (seat) return; // The server replies with a fresh seat list.

    GameSocket.clearSession();
    resetHomeDraft();
    store.clearRoom();
  },

  setDeck(counts: Partial<Record<RoleId, number>>) {
    sendAsHost({ t: "set_deck", deck: counts });
  },

  setSettings(patch: Partial<RoomSettings>) {
    if (typeof patch.narrationEnabled === "boolean") narrator.setEnabled(patch.narrationEnabled);
    sendAsHost({ t: "set_settings", settings: patch });
  },

  kickPlayer(playerId: PlayerId) {
    sendAsHost({ t: "kick_player", playerId });
  },

  startGame() {
    sendAsHost({ t: "start_game" });
  },

  ready(seat) {
    sendAs(seat, { t: "ready" });
    // Hand the phone back straight away, without waiting for the echo: on a
    // shared device the next thing on screen must be the gate for the other
    // player, never this one's card a second time.
    store.markAcknowledged(seat);
    store.lockSeats();
  },

  tapSlot(seat, slot: CardSlot) {
    const turn = store.snapshotFor(seat)?.private?.turn;
    if (!turn || turn.resolved) return;

    const result = applyTap(turn, store.state.selection, slot);
    if (result.submit) {
      sendAs(seat, {
        t: "night_action",
        groupId: result.submit.groupId,
        slots: result.submit.slots,
      });
    }
    store.setSelection(result.selection);
  },

  skipNight(seat) {
    sendAs(seat, { t: "night_skip" });
  },

  endDiscussion() {
    sendAsHost({ t: "end_discussion" });
  },

  stopRound() {
    // Cut whatever the narrator is halfway through. Lines queue rather than
    // interrupt, so without this "ouvrez les yeux" would wait behind the
    // long line that just called a role - with the table still asleep.
    narrator.cancel();
    store.setConfirmingStop(false);
    sendAsHost({ t: "stop_round" });
  },

  castVote(seat, targetId: PlayerId) {
    sendAs(seat, { t: "cast_vote", targetId });
  },

  playAgain() {
    sendAsHost({ t: "play_again" });
  },

  testVoice() {
    narrator.sample();
  },

  setVoice(voice) {
    narrator.setVoice(voice);
    store.setVoice(voice);
    // Choosing a voice is asking to hear it.
    narrator.sample();
  },
};

const render = createRenderer(root, store, actions);
store.subscribe(render);

function handleMessage(message: ServerMessage): void {
  switch (message.t) {
    case "welcome":
      GameSocket.saveSession({ code: message.code, seats: message.seats });
      store.setSeats(message.seats.map((seat) => seat.playerId));
      return;

    case "state":
      store.applyServerState(message.state);
      // Narration is a host-device setting, and the host can change between
      // rounds, so it is re-applied from every snapshot rather than once.
      narrator.setEnabled(store.isHost && message.state.settings.narrationEnabled);
      return;

    case "narrate":
      narrator.speak(message.key, message.params ?? {});
      return;

    case "error":
      store.setError(errorMessage(message.code));
      return;

    case "goodbye":
      // One seat is gone, or all of them: stale credentials, a kick, a seat
      // reclaimed by another device, or a deliberate exit.
      if (message.seat) {
        GameSocket.dropSeat(message.seat);
        store.dropSeat(message.seat);
      } else {
        GameSocket.clearSession();
        narrator.cancel();
        resetHomeDraft();
        store.clearRoom();
      }
      if (message.reason !== "left") store.setError(errorMessage(message.reason));
      return;

    case "pong":
      return;
  }
}

// A shared link such as https://example.com/ABCD lands here; lift the code out
// of the path so the player only has to type a nickname.
const deepLink = location.pathname.slice(1).toUpperCase();
if (new RegExp(`^[A-Z]{${ROOM_CODE_LENGTH}}$`).test(deepLink)) presetJoinCode(deepLink);

socket.connect();
render();

// A phone that has been asleep wakes with a socket the OS quietly killed;
// nudging it here makes the round resume as soon as the screen comes back on.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") socket.connect();
});
